import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import _ from 'lodash'
import { Box, styled } from '@mui/material'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import Dropzone from 'react-dropzone'
import Loading from '../../../process-state-notifications/Loading'
import {
  findHighLightItemsInRichText,
  getEditorExtensions,
  getMarkdownFromRichEditor,
  serializeRichTextSliceForClipboard,
} from '../../richtext'
import { TableInteractions } from '../../richtext/TableInteractions'
import { RichTextStyleButtons } from '../../../toolbars/RichTextToolbar'
import { colorPalette } from '../../../../style/colorPalette'
import { sanitizePastedHTML } from './sanitizePastedHTML'

type RichTextViewProps = {
  loading: boolean
  toolbarSlot: HTMLElement | null
  onDropAccepted: (acceptedFiles: File[]) => void
  onPaste: (event) => void
  onPasteCapture: (event) => void
  onEditorReady: (editor: Editor) => void
  onEditorDestroy: () => void
  onContentChange: (markdown: string) => void
}

export const RichTextView = ({
  loading,
  toolbarSlot,
  onDropAccepted,
  onPaste,
  onPasteCapture,
  onEditorReady,
  onEditorDestroy,
  onContentChange,
}: RichTextViewProps) => {
  const [highlightItems, setHighlightItems] = useState<string[]>([])
  const [isInTable, setIsInTable] = useState(false)
  const [editorContainerEl, setEditorContainerEl] =
    useState<HTMLElement | null>(null)

  // useEditor の callback は生成時にキャプチャされるため、
  // 最新の callback 参照を ref 経由で読み直して stale 状態を避ける。
  const onContentChangeRef = useRef(onContentChange)
  onContentChangeRef.current = onContentChange

  // editorProps は editor 生成前に定義されるため、ref 経由で自身の editor を参照する
  const editorRef = useRef<Editor | null>(null)

  const debouncedFindHighlight = useMemo(
    () =>
      _.debounce((ed: Editor) => {
        setHighlightItems(findHighLightItemsInRichText(ed))
      }, 100),
    []
  )

  const editor = useEditor({
    extensions: getEditorExtensions(),
    content: '',
    parseOptions: {
      preserveWhitespace: 'full',
    },
    editorProps: {
      attributes: {
        class: 'richtext-editor',
      },
      // ヘッダーセル（<th>）をコピペしたとき、tableHeader ノードとして挿入され
      // CSS の `th { background-color }` が適用されて背景色が付いてしまうため、
      // ペースト時に <th> を <td> に変換しておく。
      transformPastedHTML: (html: string) => {
        return sanitizePastedHTML(html)
      },
      // コピー時の text/plain を Markdown ソースとして書き出す。
      // 既定のテキスト化では空段落が Markdown ソースエディタへのペーストで増殖する。
      clipboardTextSerializer: slice =>
        serializeRichTextSliceForClipboard(editorRef.current, slice),
      handleDOMEvents: {
        // セル自体をドラッグ&ドロップで移動できてしまうブラウザ標準動作を抑止する
        dragstart: (_view, event) => {
          const target = event.target instanceof Element ? event.target : null
          if (target?.closest('td, th')) {
            event.preventDefault()
            return true
          }
          return false
        },
      },
    },
    onUpdate: ({ editor: ed }) => {
      onContentChangeRef.current(getMarkdownFromRichEditor(ed))
      debouncedFindHighlight(ed)
      setIsInTable(ed.isActive('tableCell') || ed.isActive('tableHeader'))
    },
    onSelectionUpdate: ({ editor: ed }) => {
      debouncedFindHighlight(ed)
      setIsInTable(ed.isActive('tableCell') || ed.isActive('tableHeader'))
    },
    onTransaction: ({ editor: ed, transaction }) => {
      if (transaction.storedMarksSet) {
        debouncedFindHighlight(ed)
      }
    },
  })
  editorRef.current = editor

  useEffect(() => {
    if (!editor) return
    onEditorReady(editor)
    return () => {
      onEditorDestroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editor参照の登録/解除だけ
  }, [editor])

  return (
    <>
      {toolbarSlot &&
        createPortal(
          <RichTextStyleButtons
            editor={editor ?? undefined}
            hideHeaderFour={true}
            hideHeaderFive={true}
            hideHeaderSix={true}
            highlightItems={highlightItems}
            isInTable={isInTable}
          />,
          toolbarSlot
        )}
      <Loading isLoading={loading} />
      <Dropzone onDropAccepted={onDropAccepted} multiple={true}>
        {({ getRootProps, getInputProps }) => (
          <Box
            {...getRootProps({
              onClick: e => {
                e.stopPropagation()
              },
            })}
            sx={{
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
            }}
          >
            <input {...getInputProps()} />
            <EditorContainer ref={setEditorContainerEl}>
              <EditorContent
                editor={editor}
                onPaste={onPaste}
                onPasteCapture={onPasteCapture}
                style={{ width: '100%', height: '100%' }}
              />
            </EditorContainer>
          </Box>
        )}
      </Dropzone>
      <TableInteractions editor={editor} containerEl={editorContainerEl} />
    </>
  )
}

const EditorContainer = styled('div')({
  width: '100%',
  height: '100%',
  // 単一セル選択時のカーソル枠。クラスは activeCellHighlightExtension が
  // ProseMirror Decoration として付与する。
  // 複数セル選択時は ProseMirror 標準の .selectedCell 装飾（範囲全体を覆う背景）に任せる。
  '& td.active-cell, & th.active-cell': {
    outline: `2px solid ${colorPalette.skyBlue[4]}`,
    outlineOffset: '-2px',
  },
})
